import { useEffect, useMemo } from "react"
import { useTick } from "@nova/toolkit-pure-hooks"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectWeatherPanel, selectWeatherTimeline } from "features/map/control/selectors"
import { setWeatherCurrentTime } from "features/map/control/slice"
import { WeatherMode } from "features/map/control/types"
import { useWeatherManifestGeneration } from "./weatherTileSource"
import { forwardHorizonMs, historyStartMs } from "./weatherWindow"

const HOUR_MS = 60 * 60 * 1000
const MINUTE_MS = 60 * 1000
const TICK_POLL_MS = 30 * 1000

const quantize = (ms: number) => Math.floor(ms / MINUTE_MS) * MINUTE_MS
const floorHour = (ms: number) => Math.floor(ms / HOUR_MS) * HOUR_MS

export const useWeatherNowMs = () => {
    useTick(TICK_POLL_MS)
    return quantize(Date.now())
}

export const weatherTimelineWindow = (mode: WeatherMode, nowMs: number) => ({
    startMs: floorHour(historyStartMs(mode, nowMs)),
    endMs: Math.max(floorHour(nowMs), floorHour(forwardHorizonMs(mode, nowMs))),
})

export const useWeatherTimelineWindow = (mode: WeatherMode) => {
    const nowMs = useWeatherNowMs()
    const manifestGeneration = useWeatherManifestGeneration()
    return useMemo(
        () => ({ nowMs, ...weatherTimelineWindow(mode, nowMs) }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mode, nowMs, manifestGeneration]
    )
}

export const useWeatherPlayheadClamp = () => {
    const dispatch = useAppDispatch()
    const { mode } = useAppSelector(selectWeatherPanel)
    const { currentTime } = useAppSelector(selectWeatherTimeline)
    const { startMs, endMs } = useWeatherTimelineWindow(mode)
    const currentMs = Date.parse(currentTime)

    useEffect(() => {
        if (currentMs >= startMs && currentMs <= endMs) return
        const clamped = Math.min(endMs, Math.max(startMs, currentMs))
        dispatch(setWeatherCurrentTime(new Date(clamped).toISOString()))
    }, [currentMs, startMs, endMs, dispatch])
}
